import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useWheelbarrow } from '../context/WheelbarrowContext';
import { useAuth } from '../context/AuthContext';
import { formatGBP } from '../utils/currency';
import './Wheelbarrow.css';

const Wheelbarrow = () => {
  const { state, removeItem } = useWheelbarrow();
  const { user } = useAuth();
  const [isExpanded, setIsExpanded] = useState(false);
  const wheelbarrowRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isExpanded && 
          wheelbarrowRef.current && 
          !wheelbarrowRef.current.contains(event.target as Node)) {
        setIsExpanded(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isExpanded]);

  useEffect(() => {
    // Update main content padding based on wheelbarrow state
    const mainContent = document.querySelector('.main-content');
    if (mainContent) {
      if (isExpanded) {
        mainContent.classList.add('wheelbarrow-expanded');
      } else {
        mainContent.classList.remove('wheelbarrow-expanded');
      }
    }
  }, [isExpanded]);

  // Remove expanded class when wheelbarrow is unmounted
  useEffect(() => {
    return () => {
      const mainContent = document.querySelector('.main-content');
      if (mainContent) {
        mainContent.classList.remove('wheelbarrow-expanded');
      }
    };
  }, []);

  const totalItems = state.items.length;
  const totalCost = state.totalPence;

  const handleCheckout = () => {
    setIsExpanded(false);
    if (!user) {
      navigate('/login', { state: { from: '/checkout' } });
    } else {
      navigate('/checkout');
    }
  };

  if (totalItems === 0) return null;

  return (
    <div 
      ref={wheelbarrowRef}
      className={`wheelbarrow-container ${isExpanded ? 'expanded' : ''}`}
    >
      <div className="wheelbarrow-bar" onClick={() => setIsExpanded(!isExpanded)}>
        <div className="wheelbarrow-summary">
          <span className="wheelbarrow-icon">🛒</span>
          <span className="item-count">{totalItems} item{totalItems !== 1 ? 's' : ''}</span>
          <span className="total-amount">{formatGBP(totalCost)}</span>
        </div>
        <button className="view-button">
          {isExpanded ? 'Hide Details' : 'View Details'}
        </button>
      </div>

      {isExpanded && (
        <div className="wheelbarrow-details">
          <h3>Your Wheelbarrow</h3>
          <div className="wheelbarrow-items">
            {state.items.map((item) => (
              <div key={item.item.id} className="wheelbarrow-item">
                <div className="item-info">
                  <h4>{item.item.name}</h4>
                  <p className="rental-duration">
                    {item.days} day{item.days !== 1 ? 's' : ''} @ £{item.item.dailyRateGBP.toFixed(2)}/day
                  </p>
                </div>
                <div className="item-actions">
                  <span className="item-cost">
                    £{(item.item.dailyRateGBP * item.days).toFixed(2)}
                  </span>
                  <button 
                    onClick={() => removeItem(item.item.id)}
                    className="remove-item"
                  >
                    ×
                  </button>
                </div>
              </div>
            ))}
          </div>
          <div className="wheelbarrow-footer">
            <div className="total">
              <span>Total:</span>
              <span className="total-amount">{formatGBP(totalCost)}</span>
            </div>
            <button 
              className="checkout-button"
              onClick={handleCheckout}
            >
              {user ? 'Proceed to Checkout' : 'Sign in to Checkout'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Wheelbarrow; 